import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConversationList from './ConversationList';
import ChatWindow from './ChatWindow';
import ComingSoonBox from './ComingSoonBox';
import { MESSAGING_ENABLED } from '../config/features';
import { C, t } from '../constants/dashboardConstants';
import {
  getConversations, getMessages, sendMessage, markConversationRead,
  deleteConversation, setConversationBlocked, isMessagingUnavailable,
} from '../../api/messageApi';
import {
  fmtTime, withDateDividers, normalizeConversation,
  SEND_DENY_MESSAGE, SEND_DENY,
} from './messageUtils';

const LIST_POLL_MS = 10000;
const THREAD_POLL_MS = 4000;

/**
 * 배우·제작사 양쪽 메시지 페이지의 공통 본체.
 * 역할별 차이는 바깥 레이아웃과 제작사 전용 토글뿐이라 이 컴포넌트를 공유한다.
 */
export default function MessagesView({ headerRight }) {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingList, setLoadingList] = useState(MESSAGING_ENABLED);
  const [loadingThread, setLoadingThread] = useState(false);
  // 런칭 전이면 서버에 묻지 않고 바로 Coming Soon으로 시작한다
  const [unavailable, setUnavailable] = useState(!MESSAGING_ENABLED);
  const [error, setError] = useState(null);

  const selectedRef = useRef(null);
  selectedRef.current = selectedId;

  const selected = conversations.find(c => c.id === selectedId) ?? null;

  const loadList = useCallback(async () => {
    if (!MESSAGING_ENABLED) return;
    try {
      const list = (await getConversations()).map(c => normalizeConversation(c)).filter(Boolean);
      setConversations(list);
      setUnavailable(false);
      setError(null);
      // 처음 진입 시 첫 대화를 자동 선택
      setSelectedId(prev => (prev != null ? prev : (list[0]?.id ?? null)));
    } catch (err) {
      if (isMessagingUnavailable(err)) setUnavailable(true);
      else setError(`대화 목록을 불러오지 못했습니다. (HTTP ${err.response?.status ?? '네트워크'})`);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadThread = useCallback(async (id, { silent } = {}) => {
    if (!MESSAGING_ENABLED || id == null) return;
    if (!silent) setLoadingThread(true);
    try {
      const { messages: list } = await getMessages(id);
      // 응답이 도착하는 사이 사용자가 다른 대화로 옮겼을 수 있다
      if (selectedRef.current !== id) return;
      setMessages(withDateDividers(list));
    } catch (err) {
      if (!isMessagingUnavailable(err) && !silent) {
        setError(`메시지를 불러오지 못했습니다. (HTTP ${err.response?.status ?? '네트워크'})`);
      }
    } finally {
      if (!silent) setLoadingThread(false);
    }
  }, []);

  // 목록 폴링 — 탭이 숨겨져 있으면 쉰다
  useEffect(() => {
    if (!MESSAGING_ENABLED) return undefined;
    loadList();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') loadList();
    }, LIST_POLL_MS);
    return () => clearInterval(timer);
  }, [loadList]);

  // 선택된 대화 폴링 + 읽음 처리
  useEffect(() => {
    if (selectedId == null || unavailable) return;
    loadThread(selectedId);
    markConversationRead(selectedId).catch(() => {});
    setConversations(prev => prev.map(c => (c.id === selectedId ? { ...c, unread: 0 } : c)));

    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') loadThread(selectedId, { silent: true });
    }, THREAD_POLL_MS);
    return () => clearInterval(timer);
  }, [selectedId, unavailable, loadThread]);

  const handleSend = async (text) => {
    if (selectedId == null) return;
    const tempId = `tmp-${Date.now()}`;
    // 낙관적 표시 — 실패하면 재시도 버튼이 붙는다
    setMessages(prev => [...prev, { id: tempId, text, isMine: true, time: fmtTime(), status: 'sending' }]);
    try {
      await sendMessage(selectedId, { body: text });
      await loadThread(selectedId, { silent: true });
      loadList();
    } catch (err) {
      // 차단·수신거부는 400으로 온다. 재시도해도 계속 실패하므로
      // '전송 실패'로 두지 않고 사유를 알려주고 낙관적 말풍선을 걷어낸다.
      const status = err?.response?.status;
      const reason = err?.response?.data?.reason;
      if (status === 400 || status === 403) {
        setMessages(prev => prev.filter(m => m.id !== tempId));
        // 목록 갱신이 성공하면 setError(null)로 안내가 지워지므로, 갱신을 먼저 끝낸다
        await loadList();
        setError(SEND_DENY_MESSAGE[reason] ?? '메시지를 보낼 수 없습니다. 대화 상태를 확인해주세요.');
        return;
      }
      setMessages(prev => prev.map(m => (m.id === tempId ? { ...m, status: 'failed' } : m)));
    }
  };

  const handleRetry = (msg) => {
    setMessages(prev => prev.filter(m => m.id !== msg.id));
    handleSend(msg.text);
  };

  const handleDelete = async (conv) => {
    if (!window.confirm('이 대화를 목록에서 삭제할까요?\n상대에게는 그대로 남고, 새 메시지가 오면 다시 나타납니다.')) return;
    setConversations(prev => prev.filter(c => c.id !== conv.id));
    if (selectedId === conv.id) { setSelectedId(null); setMessages([]); }
    try {
      await deleteConversation(conv.id);
    } catch {
      setError('대화 삭제에 실패했습니다.');
      loadList();
    }
  };

  const handleToggleBlock = async (conv) => {
    const next = !conv.blocked;
    if (next && !window.confirm('이 상대를 차단할까요?\n대화는 읽기 전용으로 남습니다.')) return;
    setConversations(prev => prev.map(c => (c.id === conv.id ? { ...c, blocked: next } : c)));
    try {
      await setConversationBlocked(conv.id, next);
    } catch {
      setConversations(prev => prev.map(c => (c.id === conv.id ? { ...c, blocked: !next } : c)));
      setError('차단 상태 변경에 실패했습니다.');
    }
  };

  if (unavailable) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {headerRight}
        <ComingSoonBox />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {headerRight}

      {error && (
        <span style={t('14px', 500, '#EF4444')}>{error}</span>
      )}

      <div style={{ display: 'flex', gap: '20px', alignItems: 'stretch', minHeight: '620px' }}>
        <ConversationList
          conversations={conversations}
          selectedId={selectedId}
          onSelect={(conv) => setSelectedId(conv.id)}
          onDelete={handleDelete}
          onToggleBlock={handleToggleBlock}
          emptyText={loadingList ? '불러오는 중...' : '아직 대화가 없습니다.'}
        />
        <ChatWindow
          conversation={selected}
          messages={messages}
          loading={loadingThread}
          onSend={handleSend}
          onRetry={handleRetry}
          readOnly={Boolean(selected?.blocked || selected?.peerBlockedMe)}
          readOnlyNotice={
            selected?.peerBlockedMe
              ? SEND_DENY_MESSAGE[SEND_DENY.BLOCKED]
              : selected?.blocked ? SEND_DENY_MESSAGE[SEND_DENY.I_BLOCKED] : undefined
          }
          onOpenPosting={() => {
            if (selected?.posting?.id) navigate(`/castings/${selected.posting.id}`);
          }}
        />
      </div>
    </div>
  );
}

export { C };
